// DataComponent.tsx
import { useState, useMemo } from 'react';
import { useDataContext } from './Contexts/DataContext';
import { ComboBox, Item, Key, ProgressCircle, Text } from '@adobe/react-spectrum';
import { DataComponentProps } from './DataComponentProps';
import { useTranslation } from 'react-i18next';

interface ContactDropDownProps extends DataComponentProps {
  apiOptions: any;
}
const ContactDropDown: React.FC<ContactDropDownProps> = ({ compKey, label, width, isRequired, onValueChange, apiOptions }) => {
  const { t } = useTranslation();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const message = _t('is_required').split('[content]').join(label);
  const [errorMessage, setErrorMessage] = useState(isRequired ? message : '');
  const [value, setValue] = useState<Key | null>('');
  const [input, setInput] = useState('');
  const { getContactData } = useDataContext();
  const { data, error, loading, loadingMore, hasMore, totalCount } = getContactData(apiOptions);

  // Filter data based on input for better search experience
  const filteredData = useMemo(() => {
    if (!input.trim()) return data;
    
    const searchTerm = input.toLowerCase();
    return data.filter(item => {
      const fullName = `${item.name1} ${item.name2 ?? ''} ${item.name3 ?? ''}`.toLowerCase();
      return fullName.includes(searchTerm);
    });
  }, [data, input]);

  const onSelectionChange = (new_value: Key | null) => {
    if(onValueChange)
        onValueChange(new_value ? new_value.toString() : '', input);
    setValue(new_value);
    // if(isRequired && !value) 
    //   setErrorMessage(message);
    // else 
    //   setErrorMessage('');
  };

  const onInputChange = (new_value: string) => {
    setInput(new_value);
    if(isRequired && !new_value) 
      setErrorMessage(message);
    else 
      setErrorMessage('');

    if(onValueChange)
      onValueChange(value ? value.toString() : '', new_value);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px' }}>
        <ProgressCircle size="S" isIndeterminate />
        <Text>Loading contacts...</Text>
      </div>
    );
  }
  
  if (error) return <div>Error: {error}</div>;

  return (
    <ComboBox 
        key={compKey} 
        label={label} 
        width={width} 
        isRequired={isRequired} 
        selectedKey={value}
        onSelectionChange={onSelectionChange} 
        onInputChange={onInputChange}
        validationState={errorMessage ? 'invalid' : undefined}
        errorMessage={errorMessage}
        allowsCustomValue
        menuTrigger="focus"
        >
      {[
        ...filteredData.map((item) => {
          const fullName = `${item.name1} ${item.name2 ?? ''} ${item.name3 ?? ''}`.trim();
          return (
            <Item key={item.id} textValue={fullName}>
              {fullName}
            </Item>
          );
        }),
        ...(loadingMore ? [
          <Item key="loading-more" textValue="Loading more contacts...">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px' }}>
              <ProgressCircle size="S" isIndeterminate />
              <Text>Loading more contacts...</Text>
            </div>
          </Item>
        ] : []),
        ...(hasMore && !loadingMore ? [
          <Item key="more-available" textValue={totalCount > 0 ? `${filteredData.length} of ${totalCount} contacts shown` : 'More contacts available'}>
            <Text>
              {totalCount > 0 ? `${filteredData.length} of ${totalCount} contacts shown` : 'More contacts available'}
            </Text>
          </Item>
        ] : [])
      ]}
    </ComboBox>
  );
};

export default ContactDropDown;
